import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Injector,
  Input,
  ViewChild,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable, timer } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { CalibrationData } from '../../../../../team-apis/otterdam/calibration-data';
import { OutputKeyboardShortcutService } from '../utils/output-keyboard-shortcut.service';

export const zoomPanelFocusSettingKey = 'zoomPanel:focus';

@Component({
  selector: 'h3-zoom-panel',
  templateUrl: './zoom-panel.component.html',
  styleUrls: ['./zoom-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ZoomPanelComponent extends DirectiveSuperclass {
  @Input() primaryOutputInjector?: Injector;
  @Input() calibrationData$?: Observable<CalibrationData | undefined>;
  @Input() isHidden = true;
  @ViewChild('panel') private panelRef!: ElementRef;

  constructor(
    injector: Injector,
    private store: AbstractAnnotationStore,
    private keyboardShortcutService: OutputKeyboardShortcutService,
  ) {
    super(injector);

    this.store('focus').set(this.getPreferredFocus());

    this.subscribeTo(this.store('focus').$.pipe(filter(isDefined)), (focus) =>
      localStorage.setItem(zoomPanelFocusSettingKey, focus),
    );

    // Need to delay setting focus on macroqueue or else the focusable element
    // is not rendered yet
    this.subscribeTo(
      timer(0).pipe(switchMap(() => this.store('focus').$)),
      (focus) => {
        if (focus === 'zoomPanel') {
          this.panelRef.nativeElement.focus({ preventScroll: true });
        }
      },
    );
  }

  @HostBinding('attr.hidden')
  get hiddenAttr() {
    return this.isHidden ? '' : null;
  }

  onKeyboardEvent(event: KeyboardEvent) {
    this.keyboardShortcutService.handleKeyboardEvent(event);
  }

  handleFocusOut() {
    this.store('focus').set('main');
  }

  handleFocusIn() {
    this.store('focus').set('zoomPanel');
  }

  private getPreferredFocus() {
    return localStorage.getItem(zoomPanelFocusSettingKey) === 'zoomPanel'
      ? 'zoomPanel'
      : 'main';
  }
}
